import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchDeviations,
  fetchDeviation,
  updateDeviation,
  createDeviation,
  deleteDeviation,
} from "../../api/quality/deviation";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  totalCount: 0,
  currentCount: 0,
  newCount: 0,
  emailFailedCount: 0,
};

export const initialController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteDeviationAsync = createAsyncThunk(
  "deviations/deleteDeviationAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteDeviation(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_deviation");
    }
  }
);

export const createDeviationAsync = createAsyncThunk(
  "deviations/createDeviationAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createDeviation(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_deviation");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_deviation");
    }
  }
);

export const updateDeviationAsync = createAsyncThunk(
  "deviations/updateDeviationAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateDeviation(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_deviation");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_deviation");
    }
  }
);

export const getDeviationsAsync = createAsyncThunk(
  "deviations/getDeviationsAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchDeviations(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_deviations");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_deviations");
    }
  }
);

export const getDeviationAsync = createAsyncThunk(
  "deviations/getDeviationAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchDeviation(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_deviation");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_deviation");
    }
  }
);

const deviationSlice = createSlice({
  name: "deviations",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getDeviationsAsync.pending, (state, action) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
        if (!action.meta?.arg?.append) {
          state.data = [];
          state.totalCount = 0;
          state.currentCount = 0;
        }
      })
      .addCase(getDeviationsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.meta?.arg?.append) {
          const existing = new Set((state.data || []).map((d) => d.id));
          const incoming = (action.payload.data || []).filter(
            (d) => !existing.has(d.id)
          );
          state.data = [...(state.data || []), ...incoming];
        } else {
          state.data = action.payload.data;
        }
        state.totalCount = action.payload.totalCount;
        state.currentCount = action.payload.currentCount;
        state.newCount = action.payload.newCount;
        state.emailFailedCount = action.payload.emailFailedCount || 0;
      })
      .addCase(getDeviationsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente avvikene.");
      })
      .addCase(updateDeviationAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateDeviationAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const index = draftState.data.findIndex(
            (type) => type.id === action.payload.id
          );
          draftState.data[index] = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(updateDeviationAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere avviket.");
      })
      .addCase(createDeviationAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createDeviationAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
        });
      })
      .addCase(createDeviationAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette avviket.");
      })
      .addCase(deleteDeviationAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteDeviationAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(deleteDeviationAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke slette avviket.");
      })
      .addCase(getDeviationAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getDeviationAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.deviation = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getDeviationAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente avviket.");
      });
  },
});

export const { reducer } = deviationSlice;

export default deviationSlice;
